import type { Dispatch } from 'redux';
import { getProducts, getProductsNames } from '../api/products';
import {
  getCompletedOrders,
  getPartialOrders,
  getPendingOrders,
  getEmployeeRecentOrders,
} from '../api/orders';
import { getRecentOrders } from '../api/backofficeOrders';
import { addupdateProducts } from '../redux/productSlice';
import {
  addCompletedOrder,
  addPartialOrder,
  addpending,
  clearCompletedOrders,
  clearPartialOrders,
  clearPendingOrders,
} from '../redux/OrdersSlice';
import {
  setRecentTransactions,
  setRecentTransactionsLoading,
  setRecentTransactionsError,
} from '../redux/backoffice/recentTransactionsSlice';
import {
  setEmployeeRecentTransactions,
  setEmployeeRecentTransactionsLoading,
  setEmployeeRecentTransactionsError,
} from '../redux/backoffice/employeeRecentTransactionsSlice';
import { PRODUCT_LIMIT } from '../CONSTANTS';
import {
  getLocationEpoch,
  isCurrentLocationEpoch,
} from '../utils/locationEpoch';

type Auth = { accessToken?: string | null };

export function reconcileProducts(
  axiosPrivate: any,
  auth: Auth,
  dispatch: Dispatch,
) {
  const epoch = getLocationEpoch();
  getProducts(axiosPrivate, 1, PRODUCT_LIMIT, auth)
    .then((res: any) => {
      if (!isCurrentLocationEpoch(epoch)) return;
      if (res?.data?.status === 200) {
        dispatch(addupdateProducts(res.data.data));
      }
    })
    .catch(() => {});
}

export function reconcileOrders(
  axiosPrivate: any,
  auth: Auth,
  dispatch: Dispatch,
) {
  const epoch = getLocationEpoch();

  // One bulk merge per bucket, same as HomeScreen's initial load. Dispatching
  // upsertOrderDoc per document made reconcile O(docs²) and froze the UI.
  const applyPage =
    (clearBucket: () => any, mergePage: (page: any) => any) =>
    (res: any) => {
      if (!isCurrentLocationEpoch(epoch)) return;
      if (res?.data?.status !== 200) return;
      const page = res?.data?.data;
      if (!Array.isArray(page?.docs)) return;
      // Page 1 replaces the list so orders deleted or moved while the stream
      // was down drop out. Cleared only on success, so a failure keeps the list.
      dispatch(clearBucket());
      dispatch(mergePage(page));
    };

  getCompletedOrders(axiosPrivate, 1, auth)
    .then(applyPage(clearCompletedOrders, addCompletedOrder))
    .catch(() => {});

  getPartialOrders(axiosPrivate, 1, auth)
    .then(applyPage(clearPartialOrders, addPartialOrder))
    .catch(() => {});

  getPendingOrders(axiosPrivate, 1, auth)
    .then(applyPage(clearPendingOrders, addpending))
    .catch(() => {});
}

export function reconcileOwnerRecentTransactions(
  axiosBackoffice: any,
  ownerId: string,
  ownerAccessToken: string,
  dispatch: Dispatch,
) {
  const epoch = getLocationEpoch();
  dispatch(setRecentTransactionsLoading(true));
  dispatch(setRecentTransactionsError(null));
  getRecentOrders(axiosBackoffice, ownerId, 1, 50, {
    accessToken: ownerAccessToken,
  })
    .then((res: any) => {
      if (!isCurrentLocationEpoch(epoch)) return;
      if (res?.status === 200) {
        const raw = res?.data?.data ?? res?.data;
        const docs = raw?.docs ?? raw?.data ?? [];
        const page = raw?.page ?? 1;
        const totalPages = raw?.totalPages ?? 1;
        dispatch(
          setRecentTransactions({ docs, page, totalPages } as any),
        );
      } else {
        dispatch(
          setRecentTransactionsError(
            String(res?.data?.message || 'Failed to fetch recent orders'),
          ),
        );
      }
    })
    .catch((e: any) => {
      if (!isCurrentLocationEpoch(epoch)) return;
      dispatch(
        setRecentTransactionsError(
          e?.message || 'Failed to fetch recent transactions',
        ),
      );
    })
    .finally(() => {
      if (!isCurrentLocationEpoch(epoch)) return;
      dispatch(setRecentTransactionsLoading(false));
    });
}

export function reconcileEmployeeRecentTransactions(
  axiosPrivate: any,
  auth: Auth,
  dispatch: Dispatch,
) {
  const epoch = getLocationEpoch();
  dispatch(setEmployeeRecentTransactionsLoading(true));
  dispatch(setEmployeeRecentTransactionsError(null));
  getEmployeeRecentOrders(axiosPrivate, 1, 50, auth)
    .then((res: any) => {
      if (!isCurrentLocationEpoch(epoch)) return;
      if (res?.status === 200) {
        const raw = res?.data?.data ?? res?.data;
        const docs = raw?.docs ?? raw?.data ?? [];
        const page = raw?.page ?? 1;
        const totalPages = raw?.totalPages ?? 1;
        dispatch(
          setEmployeeRecentTransactions({ docs, page, totalPages } as any),
        );
      } else {
        dispatch(
          setEmployeeRecentTransactionsError(
            String(res?.data?.message || 'Failed to fetch recent orders'),
          ),
        );
      }
    })
    .catch((e: any) => {
      if (!isCurrentLocationEpoch(epoch)) return;
      dispatch(
        setEmployeeRecentTransactionsError(
          e?.message || 'Failed to fetch recent transactions',
        ),
      );
    })
    .finally(() => {
      if (!isCurrentLocationEpoch(epoch)) return;
      dispatch(setEmployeeRecentTransactionsLoading(false));
    });
}
